import { inventoryCategoryPair } from "@farnsworth/shared";
import { useState } from "react";
import { WHATNOT_DEFAULTS } from "@farnsworth/shared";
import { Badge, Button, Modal, Spinner } from "./ui.js";
import { useFunkoDetails, usePriceHistory } from "../hooks/useInventory.js";
import { GtPoppediaPicker } from "./GtPoppediaPicker.js";
import { formatCurrency, formatDate, formatDateTime } from "../lib/format.js";
import type { Category, Item } from "../types.js";

function toNumber(value: string | null): number {
  if (value === null) return 0;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  const display =
    value === null || value === undefined || value === "" ? "—" : String(value);
  return (
    <div className="flex items-baseline justify-between gap-4 py-1 text-sm">
      <span className="shrink-0 text-slate-400">{label}</span>
      <span className="text-right text-slate-100">{display}</span>
    </div>
  );
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="mb-1 border-b border-slate-800 pb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
        {title}
      </h3>
      {children}
    </div>
  );
}

/**
 * Read-only view of an item: large image plus every field, no form controls.
 */
export function ItemDetailDialog({
  item,
  categories,
  onClose,
  onEdit,
}: {
  item: Item;
  categories: Category[];
  onClose: () => void;
  onEdit: () => void;
}) {
  const funkoQuery = useFunkoDetails(item.id);
  const historyQuery = usePriceHistory(item.id);
  const [activeImage, setActiveImage] = useState(0);
  const [showMetadata, setShowMetadata] = useState(false);

  const images = item.imageUrls
    .map((url, index) => ({ url, index }))
    .filter((image) => Boolean(image.url));
  const currentImage = images[Math.min(activeImage, Math.max(images.length - 1, 0))];
  const categoryPair = inventoryCategoryPair(categories, item.categoryId);
  const showFunko = ["Funko Pop!", "Funko Soda"].includes(categoryPair.subCategory);
  const categoryName = [categoryPair.category, categoryPair.subCategory].filter(Boolean).join(" → ") || null;

  const cost = toNumber(item.purchasePrice);
  const value = toNumber(item.currentValue);
  const margin = value - cost;

  const storedWhatnot = (item.metadata?.whatnot ?? {}) as Record<string, unknown>;
  const whatnotOverrides = Object.entries(storedWhatnot).filter(
    ([, entry]) => typeof entry === "string" && entry.trim() !== "",
  );

  const legacyId = item.metadata?.legacyId;

  return (
    <Modal title={item.name} onClose={onClose} wide>
      <div className="space-y-5">
        <div className="grid gap-5 sm:grid-cols-[280px_1fr]">
          <div className="space-y-2">
            {currentImage ? (
              <img
                src={`/api/items/${item.id}/images/${currentImage.index}`}
                alt={item.name}
                className="aspect-square w-full rounded-lg border border-slate-800 bg-slate-900 object-contain"
                onError={(event) => {
                  (event.target as HTMLImageElement).style.visibility = "hidden";
                }}
              />
            ) : (
              <div className="flex aspect-square w-full items-center justify-center rounded-lg border border-slate-800 bg-slate-900 text-sm text-slate-400">
                No image
              </div>
            )}

            {images.length > 1 ? (
              <div className="flex flex-wrap gap-2">
                {images.map((image, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setActiveImage(index)}
                    className={
                      "h-12 w-12 overflow-hidden rounded border " +
                      (index === activeImage
                        ? "border-indigo-500"
                        : "border-slate-700 hover:border-slate-500")
                    }
                  >
                    <img src={`/api/items/${item.id}/images/${image.index}`} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            ) : null}
          </div>

          <div className="space-y-4">
            <div className="space-y-1">
              <div className="flex flex-wrap items-center gap-2">
                {item.sold ? <Badge tone="red">Sold</Badge> : <Badge tone="green">Available</Badge>}
                {item.isWebsiteFeatured ? <Badge tone="indigo">Featured on website</Badge> : null}
              </div>
              <p className="text-sm text-slate-400">
                {[item.series, item.boxNo ? `#${item.boxNo}` : "", item.exclusiveTo]
                  .filter(Boolean)
                  .join(" · ") || "No series details"}
              </p>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <DetailSection title="General">
                <DetailRow label="Category" value={categoryName} />
                <DetailRow label="Condition" value={item.condition} />
                <DetailRow label="Hazmat" value={item.hazmat} />
                <DetailRow label="Barcode" value={item.barcode} />
                <DetailRow label="SKU" value={item.sku} />
              </DetailSection>

              <DetailSection title="Storage & stock">
                <DetailRow label="Location" value={item.location} />
                <DetailRow label="Owner" value={item.owner} />
                <DetailRow label="Shipping weight" value={item.popWeight} />
                <DetailRow label="Quantity" value={item.quantity} />
                <DetailRow label="Last checked" value={formatDate(item.lastChecked)} />
              </DetailSection>

              <DetailSection title="Valuation">
                <DetailRow label="Purchase price" value={formatCurrency(item.purchasePrice)} />
                <DetailRow label="Current value" value={formatCurrency(item.currentValue)} />
                <DetailRow label="Asking price" value={formatCurrency(item.forSalePrice)} />
                <DetailRow
                  label="Margin"
                  value={
                    item.purchasePrice || item.currentValue
                      ? `${formatCurrency(margin.toFixed(2))}`
                      : "—"
                  }
                />
              </DetailSection>

              <DetailSection title="Sale">
                <DetailRow label="Status" value={item.sold ? "Sold" : "Available"} />
                {item.sold ? (
                  <>
                    <DetailRow label="Sold date" value={formatDate(item.soldDate)} />
                    <DetailRow label="Sold where" value={item.soldWhere} />
                    <DetailRow label="Sold price" value={formatCurrency(item.soldPrice)} />
                  </>
                ) : item.forSaleListings.length > 0 ? (
                  <div className="py-1 text-sm">
                    <span className="text-slate-400">Listed on</span>
                    <ul className="mt-1 space-y-0.5">
                      {item.forSaleListings.map((listing, index) => (
                        <li key={index} className="text-slate-100">
                          {listing.platform || "Unknown"}
                          {listing.url ? (
                            <a
                              href={listing.url}
                              target="_blank"
                              rel="noreferrer"
                              className="ml-2 text-indigo-400 hover:text-indigo-300"
                            >
                              open
                            </a>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </DetailSection>
            </div>

            <DetailSection title="Price history">
              {historyQuery.isLoading ? (
                <Spinner label="Loading history…" />
              ) : historyQuery.isError ? (
                <p className="py-1 text-sm text-rose-300">
                  {(historyQuery.error as Error).message}
                </p>
              ) : (historyQuery.data ?? []).length === 0 ? (
                <p className="py-1 text-sm text-slate-400">
                  No recorded value changes yet.
                </p>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase tracking-wide text-slate-400">
                      <th className="py-1 font-medium">Date</th>
                      <th className="py-1 font-medium">Source</th>
                      <th className="py-1 text-right font-medium">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(historyQuery.data ?? []).map((entry) => (
                      <tr key={entry.id} className="border-t border-slate-800/60">
                        <td className="py-1 text-slate-300">
                          {formatDateTime(entry.recordedAt)}
                        </td>
                        <td className="py-1 text-slate-400">{entry.source}</td>
                        <td className="py-1 text-right text-slate-100">
                          {formatCurrency(entry.price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </DetailSection>

            {item.description ? (
              <DetailSection title="Description">
                <p className="whitespace-pre-wrap py-1 text-sm text-slate-200">
                  {item.description}
                </p>
              </DetailSection>
            ) : null}
          </div>
        </div>

        <div className="grid gap-4 border-t border-slate-800 pt-4 sm:grid-cols-2">
          {showFunko ? <DetailSection title="Funko details">
            <DetailRow label="License" value={item.license} />
            <DetailRow label="Series" value={item.series} />
            <DetailRow label="Box number" value={item.boxNo} />
            <DetailRow label="Exclusive to" value={item.exclusiveTo} />
            {funkoQuery.isLoading ? (
              <Spinner label="Loading…" />
            ) : funkoQuery.data ? (
              <>
                <DetailRow label="Features" value={(funkoQuery.data.features ?? []).join(", ")} />
                <DetailRow label="PPG price" value={formatCurrency(funkoQuery.data.ppgPrice)} />
                <DetailRow label="Chase" value={funkoQuery.data.isChase ? "Yes" : "No"} />
                <DetailRow label="Vaulted" value={funkoQuery.data.isVaulted ? "Yes" : "No"} />
              </>
            ) : (
              <p className="py-1 text-sm text-slate-400">No Funko details recorded.</p>
            )}
          </DetailSection> : null}

          <DetailSection title="Whatnot listing">
            {whatnotOverrides.length > 0 ? (
              whatnotOverrides.map(([key, entry]) => (
                <DetailRow key={key} label={key} value={String(entry)} />
              ))
            ) : (
              <p className="py-1 text-sm text-slate-400">
                Using defaults ({WHATNOT_DEFAULTS.category} / {WHATNOT_DEFAULTS.subCategory}).
              </p>
            )}
          </DetailSection>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-800 pt-4 text-xs text-slate-400">
          <span>
            Added {formatDateTime(item.createdAt)} · Updated {formatDateTime(item.updatedAt)}
            {legacyId ? ` · Imported (${String(legacyId)})` : ""}
          </span>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={onClose}>
              Close
            </Button>
            <Button variant="secondary" onClick={() => setShowMetadata(true)}>
              Funko Metadata
            </Button>
            <Button onClick={onEdit}>Edit item</Button>
          </div>
        </div>
      </div>

      {showMetadata ? (
        <GtPoppediaPicker
          item={item}
          onClose={() => setShowMetadata(false)}
          onApplied={() => setShowMetadata(false)}
        />
      ) : null}
    </Modal>
  );
}
